// Bailey's inbox data: the conversation list (paged, filtered, searched) and
// one conversation (read, reply, take over, hand back). Shared by the inbox
// page and the Bailey tab on a client's page, so both behave the same.
import { useCallback, useEffect, useRef, useState } from 'react';
import { conciergeService } from '../../_services/concierge.service';
import { createPoller } from '../../_utils/poller';
import { subscribe, getArea, versionOf } from '../../_utils/syncSummary';

const PAGE = 30;
// The list refetches only when the app's routine poll (/sync/summary) says
// Bailey's conversations changed; while the list is open that poll runs
// every 20s, backing off to 60s while nothing changes.
const LIST_POLL_MS = 20000;
const LIST_POLL_MAX_MS = 60000;
// An open conversation: every 5s, backing off to 20s while nothing new
// arrives; back to 5s on anything new or anything the person does.
const THREAD_POLL_MS = 5000;
const THREAD_POLL_MAX_MS = 20000;

// What makes an open conversation "changed" for the poll's back-off.
export const threadSignature = (c) => (c
    ? [String(c._id), c.lastActivityAt, (c.messages || []).length, !!c.needsReply, !!(c.takeover && c.takeover.active), !!c.ended].join('|')
    : '');

// Has the routine poll's answer moved past what this list last loaded?
export const listIsStale = (data, event, loaded) => ['conciergeInbox', 'concierge'].some((a) => {
    const v = data[a];
    if (!v) return false;
    if (v.legacy) return event.changed.includes(a);
    return v.version !== loaded[a];
});

// The API answers errors with HTTP 200 and statusCode 0; throw on those.
export const unwrap = (res) => {
    const body = (res && res.data) || {};
    if (body.statusCode === 0) {
        throw new Error((body.error && (body.error.responseMessage || body.error.message)) || 'That didn’t work');
    }
    return (body.responseData !== undefined ? body.responseData : body.data) || {};
};

export const FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'needs', label: 'Needs you' },
    { id: 'taken', label: 'With you' },
    { id: 'booked', label: 'Booked' },
    { id: 'clients', label: 'Clients' },
    { id: 'guests', label: 'Guests' },
    { id: 'voice', label: 'Phone' }
];

// The filter chip as the API's parameters. "Phone" is a channel, not a filter.
export const paramsFor = ({ filter, q, clientUserId }) => {
    const p = { limit: PAGE };
    if (filter === 'voice') p.channel = 'voice';
    else if (filter && filter !== 'all') p.filter = filter;
    if (q && q.trim()) p.q = q.trim();
    if (clientUserId) p.clientUserId = clientUserId;
    return p;
};

// A UK number as people write it: +447474943608 → 07474 943608.
export const prettyPhone = (e164) => {
    const d = String(e164 || '').replace(/\s+/g, '');
    const mob = /^\+447(\d{3})(\d{6})$/.exec(d);
    if (mob) return `07${mob[1]} ${mob[2]}`;
    // 020 London, and the big cities' 01x1 codes (0113, 0121, 0161…).
    const lon = /^\+442(\d)(\d{4})(\d{4})$/.exec(d);
    if (lon) return `02${lon[1]} ${lon[2]} ${lon[3]}`;
    const city = /^\+441(\d)1(\d{3})(\d{4})$/.exec(d);
    if (city) return `01${city[1]}1 ${city[2]} ${city[3]}`;
    const land = /^\+44(\d{2,4})(\d{6,8})$/.exec(d);
    return land ? `0${land[1]} ${land[2]}` : String(e164 || '');
};

// Who a row is, in words: their name, the number that called, or a guest.
export const whoOf = (row) => {
    const name = row && row.clientName;
    if (name) return /^\+\d{8,}$/.test(name) ? prettyPhone(name) : name;
    if (row && row.phone) return prettyPhone(row.phone);
    return row && row.channel === 'voice' ? 'A caller' : 'A guest';
};

// A call's length in words: 184 → "3 min".
export const callLength = (seconds) => {
    const s = Number(seconds) || 0;
    if (!s) return '';
    return s < 60 ? `${s} sec` : `${Math.round(s / 60)} min`;
};

// Merge a fresh first page over what's loaded, keeping older pages below it.
export const mergeFirstPage = (fresh, current) => {
    const seen = new Set(fresh.map((r) => String(r._id)));
    const tail = current.slice(fresh.length).filter((r) => !seen.has(String(r._id)));
    return fresh.concat(tail);
};

/** The list: first page, older pages, a quiet refresh while it's open. */
export function useInbox({ filter = 'all', q = '', clientUserId = null, enabled = true } = {}) {
    const [rows, setRows] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [next, setNext] = useState(null);
    const [busyMore, setBusyMore] = useState(false);
    const key = `${filter}|${q}|${clientUserId || ''}`;
    const keyRef = useRef(key);
    keyRef.current = key;

    const loadedRef = useRef({});
    const refresh = useCallback(async (reset) => {
        const asked = keyRef.current;
        // The poll's versions as of now, before asking (a change mid-request
        // then shows as a difference next time, never hides).
        loadedRef.current = { conciergeInbox: versionOf(getArea('conciergeInbox')), concierge: versionOf(getArea('concierge')) };
        try {
            const r = unwrap(await conciergeService.conversations(paramsFor({ filter, q, clientUserId })));
            if (asked !== keyRef.current) return;
            const fresh = r.conversations || [];
            setRows((cur) => (reset ? fresh : mergeFirstPage(fresh, cur)));
            if (reset) setNext(r.hasMore ? r.next : null);
            setError('');
        } catch (e) {
            if (asked === keyRef.current) setError(e.message || 'Couldn’t load conversations just now.');
        }
        if (asked === keyRef.current) setLoaded(true);
    }, [filter, q, clientUserId]);

    useEffect(() => {
        if (!enabled) return undefined;
        setLoaded(false);
        setRows([]);
        const t = setTimeout(() => refresh(true), q ? 250 : 0);   // a short pause while typing
        return () => clearTimeout(t);
    }, [enabled, key]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!enabled) return undefined;
        return subscribe({
            areas: ['conciergeInbox', 'concierge'],
            cadence: { base: LIST_POLL_MS, max: LIST_POLL_MAX_MS },
            onUpdate: (data, event) => {
                if (!event.error && listIsStale(data, event, loadedRef.current)) refresh(false);
            }
        });
    }, [enabled, refresh]);

    const loadOlder = useCallback(async () => {
        if (!next || busyMore) return;
        setBusyMore(true);
        const asked = keyRef.current;
        try {
            const r = unwrap(await conciergeService.conversations({ ...paramsFor({ filter, q, clientUserId }), before: next }));
            if (asked !== keyRef.current) return;
            setRows((cur) => {
                const seen = new Set(cur.map((x) => String(x._id)));
                return cur.concat((r.conversations || []).filter((x) => !seen.has(String(x._id))));
            });
            setNext(r.hasMore ? r.next : null);
        } catch (e) {
            setError(e.message || 'Couldn’t load older conversations.');
        }
        setBusyMore(false);
    }, [next, busyMore, filter, q, clientUserId]);

    // After a reply or a hand-back, show the row's new state straight away.
    const patchRow = useCallback((row) => {
        if (!row || !row._id) return;
        setRows((cur) => cur.map((r) => (String(r._id) === String(row._id) ? { ...r, ...row } : r)));
    }, []);

    return { rows, loaded, error, more: !!next, busyMore, loadOlder, refresh, patchRow };
}

/** One conversation: read it, watch it while it's open, reply, take over. */
export function useConversation(id, { onChange } = {}) {
    const [convo, setConvo] = useState(null);
    const [error, setError] = useState('');
    const [sending, setSending] = useState(false);
    const idRef = useRef(id);
    idRef.current = id;
    const changed = useRef(onChange);
    changed.current = onChange;

    const apply = (r) => {
        const c = r && r.conversation;
        if (!c || String(c._id) !== String(idRef.current)) return;
        setConvo(c);
        if (changed.current) {
            const { messages, ...row } = c;
            changed.current(row);
        }
    };

    // Resolves to the conversation's signature (threadSignature), '' if none.
    const load = useCallback(async () => {
        if (!idRef.current) return '';
        try {
            const r = unwrap(await conciergeService.conversation(idRef.current));
            apply(r);
            setError('');
            return threadSignature(r && r.conversation);
        } catch (e) { setError(e.message || 'Couldn’t open that conversation.'); return ''; }
    }, []);

    const pollRef = useRef(null);
    useEffect(() => {
        setConvo(null);
        setError('');
        if (!id) return undefined;
        let last = null;
        const poller = createPoller({
            key: `concierge-thread:${id}`,
            base: THREAD_POLL_MS,
            max: THREAD_POLL_MAX_MS,
            run: async () => {
                const sig = await load();
                const changed = last !== null && sig !== '' && sig !== last;
                if (sig) last = sig;
                return changed;
            }
        });
        pollRef.current = poller;
        poller.start();
        // (reply / takeover call pollRef.current.reset(): the person did
        // something here, so watch closely again.)
        return () => { poller.stop(); if (pollRef.current === poller) pollRef.current = null; };
    }, [id, load]);

    const reply = useCallback(async (text) => {
        setSending(true);
        try { apply(unwrap(await conciergeService.replyTo(idRef.current, text))); setError(''); if (pollRef.current) pollRef.current.reset(); return true; }
        catch (e) { setError(e.message || 'Couldn’t send that. Try again.'); return false; }
        finally { setSending(false); }
    }, []);

    const takeover = useCallback(async (active) => {
        setSending(true);
        try { apply(unwrap(await conciergeService.setTakeover(idRef.current, active))); setError(''); if (pollRef.current) pollRef.current.reset(); }
        catch (e) { setError(e.message || 'Couldn’t change that just now.'); }
        finally { setSending(false); }
    }, []);

    return { convo, error, sending, reply, takeover, reload: load };
}
